// src/features/member/services/profileService.ts

import api from '../../../api/axios';

import {
  AUTH_ENDPOINTS,
  STORE_ENDPOINTS,
  USER_ENDPOINTS,
} from '../../../api';

import type {
  ApiResponse,
} from '../../../shared/types/api.types';

import type {
  DuplicateCheckResponse,
  PasswordUpdateRequest,
  ProfilePageData,
  ProfileUpdateRequest,
  StoreListItem,
  UserProfile,
} from '../types/profile.types';


export const profileService = {
  /**
   * 내 프로필 조회
   *
   * GET /api/v2/users/me
   */
  async getMyProfile():
    Promise<UserProfile> {
    const response =
      await api.get<
        ApiResponse<UserProfile>
      >(
        USER_ENDPOINTS.ME,
      );


    return response.data.data;
  },


  /**
   * 전체 매장 조회
   *
   * GET /api/v2/stores
   *
   * storeId = 0은
   * 공용 포인트 가상 매장이므로 제외
   */
  async getStores():
    Promise<StoreListItem[]> {
    const response =
      await api.get<
        ApiResponse<StoreListItem[]>
      >(
        STORE_ENDPOINTS.LIST,
      );


    return (
      response.data.data ??
      []
    ).filter(
      (
        store,
      ) =>
        store.storeId !==
        0,
    );
  },


  /**
   * 프로필 페이지 초기 데이터
   *
   * 사용자 + 매장을 동시에 조회
   */
  async getProfilePageData():
    Promise<ProfilePageData> {
    const [
      user,
      stores,
    ] =
      await Promise.all([
        this.getMyProfile(),

        this.getStores(),
      ]);


    const storeName =
      stores.find(
        (
          store,
        ) =>
          store.storeId ===
          user.storeId,
      )?.storeName ??
      user.storeName;


    return {
      user: {
        ...user,

        storeName,
      },

      stores,
    };
  },


  /**
   * 닉네임 중복 확인
   *
   * GET /api/v2/auth/check-nickname
   */
  async checkNickname(
    userNickname: string,
  ): Promise<DuplicateCheckResponse> {
    const response =
      await api.get<
        ApiResponse<DuplicateCheckResponse>
      >(
        AUTH_ENDPOINTS
          .CHECK_NICKNAME,
        {
          params: {
            userNickname,
          },
        },
      );


    return response.data.data;
  },


  /**
   * 프로필 수정
   *
   * PATCH /api/v2/users/me/profile
   */
  async updateProfile(
    request:
      ProfileUpdateRequest,
  ): Promise<UserProfile | null> {
    const response =
      await api.patch<
        ApiResponse<UserProfile>
      >(
        USER_ENDPOINTS
          .PROFILE,
        request,
      );


    return (
      response.data.data ??
      null
    );
  },


  /**
   * 비밀번호 변경
   *
   * PATCH /api/v2/users/me/password
   */
  async changePassword(
    request:
      PasswordUpdateRequest,
  ): Promise<void> {
    await api.patch(
      USER_ENDPOINTS
        .PASSWORD,
      request,
    );
  },
};